import { createTheme, responsiveFontSizes, type Theme } from '@mui/material/styles'

export type ColorMode = 'dark' | 'light'

/**
 * The same values as src/styles/tokens.css, in TS form. Both layers exist on
 * purpose: pages are written with `var(--token)` so they read like the design
 * mockup, while MUI's own components (Dialog, Select, Menu, Table) need real
 * colour values in the theme. Change a colour here and in tokens.css together.
 */
const tokens = {
  dark: {
    bg: '#081b36',
    paper: '#1f2937',
    paper2: '#2b3a4d',
    bar: '#111827',
    field: '#16202e',
    text: '#f8fafc',
    muted: '#c7d2e0',
    faint: '#8fa1b8',
    line: 'rgba(248,250,252,0.16)',
    line2: 'rgba(248,250,252,0.10)',
    hover: 'rgba(248,250,252,0.08)',
    accent: '#97D700',
    onAccent: '#0f172a',
    ok: '#67d58a',
    warn: '#ffc068',
    err: '#ff7f7f',
  },
  light: {
    bg: '#f4f7fb',
    paper: '#ffffff',
    paper2: '#eef3f9',
    bar: '#163f6b',
    field: '#f4f7fb',
    text: '#1f2a37',
    muted: '#5a6b80',
    faint: '#7b8ca3',
    line: 'rgba(31,42,55,0.14)',
    line2: 'rgba(31,42,55,0.09)',
    hover: 'rgba(31,42,55,0.06)',
    accent: '#163f6b',
    onAccent: '#f8fbff',
    ok: '#2e7d32',
    warn: '#b26a00',
    err: '#c62828',
  },
} as const

/** Amber, used for the avatar and the inbox badge in both themes. */
export const BRAND_AMBER = '#f5b301'

export function createAppTheme(mode: ColorMode): Theme {
  const t = tokens[mode]

  let theme = createTheme({
    palette: {
      mode,
      primary: { main: t.accent, contrastText: t.onAccent },
      secondary: { main: BRAND_AMBER, contrastText: '#111827' },
      success: { main: t.ok },
      warning: { main: t.warn },
      error: { main: t.err },
      background: { default: t.bg, paper: t.paper },
      text: { primary: t.text, secondary: t.muted, disabled: t.faint },
      divider: t.line,
      action: { hover: t.hover },
      po: {
        mintGreen: t.ok,
        pastelRed: t.err,
        stripedGray: t.line2,
      },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
      // The mockup titles pages at 30px/700 with tight tracking
      h4: { fontSize: 30, fontWeight: 700, letterSpacing: '-0.4px', lineHeight: 1.15 },
      h5: { fontSize: 20, fontWeight: 700, letterSpacing: '-0.2px' },
      h6: { fontSize: 15, fontWeight: 700 },
      body1: { fontSize: 14 },
      body2: { fontSize: 13 },
      caption: { fontSize: 11 },
      button: { fontWeight: 700 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: t.bg, color: t.text },
        },
      },
      // The design draws containers with a 1px border, never a drop shadow
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 12,
          },
          outlined: { borderColor: t.line },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 12, border: `1px solid ${t.line}`, boxShadow: 'none' },
        },
      },
      MuiButton: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: { borderRadius: 8, textTransform: 'none', fontWeight: 700 },
          outlined: { borderColor: t.line },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 999, fontWeight: 700, fontSize: 11 },
          outlined: { borderColor: t.line },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: t.field,
            borderRadius: 8,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.line },
          },
          input: { fontSize: 13 },
        },
      },
      MuiSelect: {
        styleOverrides: { select: { fontSize: 13 } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: t.line2, fontSize: 13 },
          head: {
            backgroundColor: t.bar,
            color: mode === 'dark' ? 'rgba(248,250,252,0.86)' : '#f8fafc',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 12, border: `1px solid ${t.line}`, backgroundImage: 'none' },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { backgroundColor: t.bar, color: '#f8fafc', fontSize: 15, fontWeight: 700 },
        },
      },
      MuiDialogActions: {
        styleOverrides: { root: { borderTop: `1px solid ${t.line}`, backgroundColor: t.field } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: t.bar, color: '#f8fafc', fontSize: 12, borderRadius: 6 },
        },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 10, fontSize: 13 } },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 700, fontSize: 13, minHeight: 44 },
        },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { borderRadius: 4, backgroundColor: t.line2 } },
      },
    },
  })

  theme = responsiveFontSizes(theme)
  return theme
}

/** Kept for callers that import a ready-made theme; dark is the default mode. */
export const theme = createAppTheme('dark')
